import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { History, RotateCcw, User } from "lucide-react";

const NOTE_LABEL = { edit: "Edited", rollback: "Restored", scheduled: "Scheduled", undo_restore: "Undo" };

const META = ["key", "updated_at", "updated_by", "updated_by_name", "_id"];
const stripMeta = (d) => {
  if (!d || typeof d !== "object") return {};
  const out = {};
  for (const k of Object.keys(d)) if (!META.includes(k)) out[k] = d[k];
  return out;
};

function fmt(iso) {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

// Version history + rollback for a single content key.
export default function HistoryDialog({ contentKey, open, onOpenChange, onRestored }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [pending, setPending] = useState(null); // version pending restore-confirm

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/content/${contentKey}/history`);
      setItems(r.data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [contentKey]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const doRestore = async () => {
    if (!pending) return;
    setRestoring(true);
    const restored = pending;
    try {
      // Capture the CURRENT live content before we overwrite it, so the editor
      // can offer a one-tap Undo of this restore.
      let prev = null;
      try {
        const cur = await api.get(`/admin/content/${contentKey}`);
        prev = stripMeta(cur.data);
      } catch { /* nothing live to capture */ }

      await api.post(`/admin/content/${contentKey}/rollback`, { version_id: restored.id });
      setPending(null);
      onOpenChange(false);
      onRestored && onRestored();

      toast.success("Restored this version \u2014 it is now live.", {
        duration: 10000,
        action: prev && Object.keys(prev).length ? {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post(`/admin/content/${contentKey}/undo-rollback`, { payload: prev });
              toast.success("Restore undone \u2014 previous content is back.");
              onRestored && onRestored();
            } catch (e) {
              toast.error(e.response?.data?.detail || "Couldn't undo the restore");
            }
          },
        } : undefined,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to restore");
    } finally {
      setRestoring(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="cms-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History size={18} className="text-[#c9a86a]" /> Change history</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[var(--muted)] -mt-2 mb-2">The last 20 versions. Restoring makes that version live immediately (and is itself saved as a new version).</p>

          {loading ? (
            <div className="space-y-2" data-testid="cms-history-loading">
              {[0, 1, 2].map((i) => <div key={i} className="h-14 rounded bg-[var(--panel-2)] animate-pulse" />)}
            </div>
          ) : !items || items.length === 0 ? (
            <div className="text-center text-[var(--muted)] py-10" data-testid="cms-history-empty">No history yet. Save a change to start tracking versions.</div>
          ) : (
            <div className="space-y-2" data-testid="cms-history-list">
              {items.map((v, i) => (
                <div key={v.id} className="flex items-center justify-between gap-3 rounded-md border border-[var(--border-lch)] bg-[var(--panel)] p-3" data-testid={`cms-history-item-${i}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-[10px] bg-[var(--panel-2)] text-[var(--text-2)] border-[var(--border-lch)]">{NOTE_LABEL[v.note] || "Saved"}</Badge>
                      {i === 0 && <Badge variant="outline" className="text-[10px] bg-[#c9a86a]/15 text-[#9a7b3f] border-[#c9a86a]/40">Current</Badge>}
                    </div>
                    <div className="text-sm text-[var(--text)] mt-1">{fmt(v.updated_at)}</div>
                    <div className="text-xs text-[var(--muted)] flex items-center gap-1"><User size={11} /> {v.updated_by_name || "Unknown"}</div>
                  </div>
                  {i !== 0 && (
                    <Button size="sm" variant="outline" onClick={() => setPending(v)} data-testid={`cms-history-restore-${i}`}>
                      <RotateCcw size={13} className="mr-1" />Restore
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!pending} onOpenChange={(o) => { if (!o) setPending(null); }}>
        <AlertDialogContent data-testid="cms-history-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Restore this version?</AlertDialogTitle>
            <AlertDialogDescription>The version from {pending ? fmt(pending.updated_at) : ""} will replace the current live content. You can restore again at any time.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cms-history-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doRestore} disabled={restoring} data-testid="cms-history-confirm-btn" className="bg-[#c9a86a] hover:bg-[#b89455] text-[var(--on-gold)]">{restoring ? "Restoring\u2026" : "Restore"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
